// Serverdaten fuer Phase 1: Rezepte, Kollektionen, Info, dazu die Knoepfe "In Meine Woche" und
// "Auf die Einkaufsliste". Alle nutzerbezogenen Schluessel beginnen mit ['nutzer', id] (siehe abfragen.ts).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import {
  api,
  type BildAuftragAntwort,
  type BildqueueStatus,
  type RezeptAenderung,
  type RezeptEingabe,
  type EinkaufRezept,
  type Info,
  type Kollektion,
  type KollektionMitRezepten,
  type PlanEintrag,
  type RezeptDetail,
  type RezeptKarte,
} from './client';

export function useNutzerId(): string {
  const { zustand } = useAuth();
  return zustand.status === 'angemeldet' ? zustand.session.user.id : 'abgemeldet';
}

export const rezeptSchluessel = {
  alle: (n: string) => ['nutzer', n, 'rezepte'] as const,
  detail: (n: string, slug: string) => ['nutzer', n, 'rezept', slug] as const,
  kollektionen: (n: string) => ['nutzer', n, 'kollektionen'] as const,
  kollektion: (n: string, id: string) => ['nutzer', n, 'kollektionen', id] as const,
};

/** Solange ein Bild aussteht, fragt die Liste alle 30 s nach (finn-architektur.md §7). */
function nachfragen(karten: readonly RezeptKarte[] | undefined): number | false {
  return karten?.some((k) => k.bild_status !== 'fertig') ? 30_000 : false;
}

export function useRezepte() {
  const n = useNutzerId();
  return useQuery({
    queryKey: rezeptSchluessel.alle(n),
    queryFn: () => api<RezeptKarte[]>('/rezepte'),
    enabled: n !== 'abgemeldet',
    staleTime: 60_000,
    refetchInterval: (q) => nachfragen(q.state.data),
  });
}

/** Detail fragt nach, solange Naehrwerte gerechnet werden (2 s) oder ein Bild entsteht (30 s). */
function detailNachfragen(d: RezeptDetail | undefined): number | false {
  if (!d) return false;
  if (d.naehrwerte_offen) return 2_000;
  return d.bild_auftrag_offen || d.bild_status !== 'fertig' ? 30_000 : false;
}

export function useRezept(slug: string) {
  const n = useNutzerId();
  return useQuery({
    queryKey: rezeptSchluessel.detail(n, slug),
    queryFn: () => api<RezeptDetail>(`/rezepte/${encodeURIComponent(slug)}`),
    enabled: n !== 'abgemeldet' && slug !== '',
    staleTime: 60_000,
    refetchInterval: (q) => detailNachfragen(q.state.data),
  });
}

/** Neues Rezept von Hand (Phase 3). */
export function useRezeptAnlegen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (daten: RezeptEingabe) => api<RezeptDetail>('/rezepte', { method: 'POST', body: JSON.stringify(daten) }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

/** Rezept speichern. 409 = jemand anderes hat inzwischen gespeichert. */
export function useRezeptSpeichern() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: ({ slug, daten }: { slug: string; daten: RezeptAenderung }) =>
      api<RezeptDetail>(`/rezepte/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(daten) }),
    onSuccess: (d) => {
      qc.setQueryData(rezeptSchluessel.detail(n, d.slug), d);
      void neuLaden();
    },
  });
}

export function useRezeptLoeschen() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (slug: string) => api<null>(`/rezepte/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
    onSuccess: (_d, slug) => {
      qc.removeQueries({ queryKey: rezeptSchluessel.detail(n, slug) });
      void neuLaden();
    },
  });
}

export function useBildNeu() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (slug: string) => api<BildAuftragAntwort>(`/rezepte/${encodeURIComponent(slug)}/bild/neu`, { method: 'POST' }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

/** Warteschlange der Bilder (Profil): Hinweis ab 48 h Wartezeit. */
export function useBildqueueStatus() {
  const n = useNutzerId();
  return useQuery({
    queryKey: ['nutzer', n, 'bildqueue'],
    queryFn: () => api<BildqueueStatus>('/bildqueue/status'),
    enabled: n !== 'abgemeldet',
    staleTime: 60_000,
  });
}

export function useKollektionen() {
  const n = useNutzerId();
  return useQuery({
    queryKey: rezeptSchluessel.kollektionen(n),
    queryFn: () => api<Kollektion[]>('/kollektionen'),
    enabled: n !== 'abgemeldet',
    staleTime: 60_000,
  });
}

// Geloeschte Kollektionen: ihre Seite ist beim Wechsel zurueck noch kurz eingehaengt und wuerde sonst nachfragen (404)
const geloeschteKollektionen = new Set<string>();

export function useKollektion(id: string) {
  const n = useNutzerId();
  return useQuery({
    queryKey: rezeptSchluessel.kollektion(n, id),
    queryFn: () => api<KollektionMitRezepten>(`/kollektionen/${encodeURIComponent(id)}`),
    enabled: n !== 'abgemeldet' && id !== '' && !geloeschteKollektionen.has(id),
    staleTime: 30_000,
    refetchInterval: (q) => nachfragen(q.state.data?.rezepte),
  });
}

export function useInfo() {
  return useQuery({ queryKey: ['info'], queryFn: () => api<Info>('/info'), staleTime: 60 * 60_000 });
}

/** Nach jeder Aenderung: Rezeptliste, Details und Kollektionen neu laden. */
function useNeuLaden() {
  const n = useNutzerId();
  const qc = useQueryClient();
  return (o?: { ausser?: readonly unknown[] }) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: rezeptSchluessel.alle(n) }),
      qc.invalidateQueries({ queryKey: ['nutzer', n, 'rezept'] }),
      qc.invalidateQueries({
        queryKey: rezeptSchluessel.kollektionen(n),
        predicate: (q) => !o?.ausser || JSON.stringify(q.queryKey) !== JSON.stringify(o.ausser),
      }),
      qc.invalidateQueries({ queryKey: ['nutzer', n, 'woche'] }),
      qc.invalidateQueries({ queryKey: ['nutzer', n, 'einkauf'] }),
    ]);
}

export function useMarkieren() {
  const n = useNutzerId();
  const qc = useQueryClient();
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: ({ slug, ...daten }: { slug: string; gemerkt?: boolean; mealprep?: boolean }) =>
      api<RezeptKarte>(`/rezepte/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(daten) }),
    // Sofort sichtbar umschalten, bei Fehler zuruecknehmen
    onMutate: async ({ slug, ...daten }) => {
      const schluessel = rezeptSchluessel.detail(n, slug);
      await qc.cancelQueries({ queryKey: schluessel });
      const vorher = qc.getQueryData<RezeptDetail>(schluessel);
      if (vorher) qc.setQueryData<RezeptDetail>(schluessel, { ...vorher, ...daten });
      const liste = qc.getQueryData<RezeptKarte[]>(rezeptSchluessel.alle(n));
      if (liste) qc.setQueryData(rezeptSchluessel.alle(n), liste.map((k) => (k.slug === slug ? { ...k, ...daten } : k)));
      return { vorher, liste };
    },
    onError: (_f, { slug }, ctx) => {
      if (ctx?.vorher) qc.setQueryData(rezeptSchluessel.detail(n, slug), ctx.vorher);
      if (ctx?.liste) qc.setQueryData(rezeptSchluessel.alle(n), ctx.liste);
    },
    onSettled: () => {
      void neuLaden();
    },
  });
}

export function useKollektionAnlegen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (name: string) => api<Kollektion>('/kollektionen', { method: 'POST', body: JSON.stringify({ name }) }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

export function useKollektionUmbenennen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<Kollektion>(`/kollektionen/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

export function useKollektionLoeschen() {
  const neuLaden = useNeuLaden();
  const n = useNutzerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<null>(`/kollektionen/${id}`, { method: 'DELETE' }),
    onSuccess: (_d, id) => {
      // Die geloeschte Kollektion nicht noch einmal abfragen (waere ein 404): ihre Seite ist beim Neuladen
      // noch eingehaengt, ein Praefix-Invalidieren von "kollektionen" wuerde sie sonst neu holen.
      const weg = rezeptSchluessel.kollektion(n, id);
      geloeschteKollektionen.add(id);
      void qc.cancelQueries({ queryKey: weg });
      void neuLaden({ ausser: weg });
      // nach dem Wechsel auf Meine Rezepte (Seite ausgehaengt) ganz entfernen
      window.setTimeout(() => qc.removeQueries({ queryKey: weg }), 0);
    },
  });
}

export function useKollektionRezept() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: ({ kollektionId, rezeptId, drin }: { kollektionId: string; rezeptId: string; drin: boolean }) =>
      api<null>(`/kollektionen/${kollektionId}/rezepte/${rezeptId}`, { method: drin ? 'PUT' : 'DELETE' }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

export function useWocheHinzufuegen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (daten: { rezept_id: string; datum?: string; portionen?: number }) =>
      api<PlanEintrag>('/woche/eintraege', { method: 'POST', body: JSON.stringify(daten) }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

export function useEinkaufHinzufuegen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (daten: { rezept_id: string; portionen?: number }) =>
      api<EinkaufRezept>('/einkauf/rezepte', { method: 'POST', body: JSON.stringify(daten) }),
    onSuccess: () => {
      void neuLaden();
    },
  });
}

/** Heutiges Datum im lokalen Kalender als YYYY-MM-DD (nicht UTC, sonst kippt es nachts um). */
export function heuteIso(): string {
  const d = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
